import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getOnsiteLiveAdminStatus, getOnsiteLiveLookups, getOnsiteLiveUser, listOnsiteLiveUsers } from '@/api/onsiteLiveAdmin';
import { useAuth } from '@/auth/useAuth';
import type { OnsiteLiveLookups, OnsiteLiveUserListItem } from '@/types/oscar';
import { MembershipsEditor, UserEditor } from '../../OnsiteLiveAdmin/UsersTab';
import { AdditionalSupervisorsEditor } from '../../OnsiteLiveAdmin/AdditionalSupervisorsEditor';
import { DepartmentGroupsPanel } from './DepartmentGroupsPanel';
import { accountStatus, type AccountStatus } from './accountStatus';

const STATUS_TONE: Record<AccountStatus['tone'], string> = {
  info: 'border-navy/30 bg-navy/5',
  warn: 'border-orange/40 bg-orange/5',
  muted: 'border-border bg-bg/50',
};

/**
 * Person › OnsiteLive — the person's OnsiteLive PERMISSIONS (plan step 7; Anthony,
 * 24/10/2026: "the Profile handles the information, the OnsiteLive tab is
 * permissions" - Oscar is the starting point).
 *
 * The details are NOT shown again here: name, email, title, mobile, work phone,
 * manager, office (from the location) and branch (the primary branch) are entered
 * on the Profile, and expiry (the termination date) on the Leaving tab; the
 * 15-minute person sync copies them to OnsiteLive. Email divert and replacement
 * manager are on the Leaving tab too (Anthony, 25/10/2026). This tab holds what
 * only OnsiteLive has: active, team (follows the department - Settings › Teams
 * and limits), subscription branch, business unit, manager if none set (External manager),
 * additional supervisors (extra OnFlow PO approvers), report
 * groups, companies and subscription groups. Admin edits after the cutover;
 * helpdesk reads.
 */
export function PersonOnsiteLive({ personId }: { personId: number }) {
  const { isAdmin } = useAuth();
  const listQuery = useQuery({
    queryKey: ['onsitelive-admin', 'users', { personId }],
    queryFn: ({ signal }) => listOnsiteLiveUsers({ personId }, signal),
  });
  const statusQuery = useQuery({ queryKey: ['onsitelive-admin', 'status'], queryFn: ({ signal }) => getOnsiteLiveAdminStatus(signal) });
  const lookupsQuery = useQuery({ queryKey: ['onsitelive-admin', 'lookups'], queryFn: ({ signal }) => getOnsiteLiveLookups(signal) });
  const canWrite = isAdmin && statusQuery.data?.WritesOpen === true;

  if (listQuery.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (listQuery.isError) return <p className="text-sm text-red">Could not load the OnsiteLive account.</p>;

  const users = listQuery.data ?? [];
  if (users.length === 0) {
    return (
      <p className="rounded border border-dashed border-border bg-white px-4 py-3 text-sm text-darkGrey/80">
        No OnsiteLive user is linked to this person. The person sync creates one within 15 minutes for an active employee or
        contractor with a username. Shared and service logins are linked from the{' '}
        <Link to="/settings/onsitelive" className="text-navy hover:underline">OnsiteLive users</Link> page instead.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-darkGrey/80">
        Name, email, title, phone, manager, office and primary branch come from this person&apos;s Profile, and the leaving date,
        email divert and replacement manager from the Leaving tab; they reach OnsiteLive within 15 minutes. This tab is what
        they can see and approve in OnsiteLive, OnBudget and OnFlow.
      </p>
      {!isAdmin && <p className="text-xs text-darkGrey/70">Read only — OnsiteLive permissions are changed by an Oscar admin.</p>}
      {lookupsQuery.isError && <p className="text-sm text-red">Could not load the groups and branch sets.</p>}
      {users.length > 1 && (
        <p role="alert" className="rounded border border-orange/40 bg-orange/5 px-3 py-2 text-sm text-darkGrey">
          <span className="font-semibold text-orange">{users.length} OnsiteLive users are linked to this person. </span>
          There should be one: ask an admin to unlink the extra accounts.
        </p>
      )}
      {users.map((u) => (
        <OnsiteLiveAccount key={u.OnsiteLiveUserId} item={u} lookups={lookupsQuery.data} canWrite={canWrite} />
      ))}
    </div>
  );
}

function OnsiteLiveAccount({ item, lookups, canWrite }: { item: OnsiteLiveUserListItem; lookups: OnsiteLiveLookups | undefined; canWrite: boolean }) {
  const q = useQuery({
    queryKey: ['onsitelive-admin', 'user', item.OnsiteLiveUserId],
    queryFn: ({ signal }) => getOnsiteLiveUser(item.OnsiteLiveUserId, signal),
  });

  if (q.isLoading) return <p className="text-sm text-darkGrey/70">Loading {item.Username}…</p>;
  if (q.isError || !q.data) return <p className="text-sm text-red">Could not load {item.Username}.</p>;
  const status = accountStatus(q.data);
  return (
    <section className="space-y-4" aria-label={`OnsiteLive permissions for ${item.Username}`}>
      {status && (
        <p role="status" className={`rounded border px-3 py-2 text-sm text-darkGrey ${STATUS_TONE[status.tone]}`}>
          {status.text}
        </p>
      )}
      <UserEditor user={q.data} lookups={lookups} canWrite={canWrite} onPerson />
      <AdditionalSupervisorsEditor userId={q.data.OnsiteLiveUserId} username={q.data.Username} canWrite={canWrite} />
      <DepartmentGroupsPanel userId={q.data.OnsiteLiveUserId} canWrite={canWrite} startDate={q.data.WaitingForStartDate} />
      <MembershipsEditor user={q.data} lookups={lookups} canWrite={canWrite} />
      <p className="text-xs text-darkGrey/70">
        The team follows the department: <Link to="/settings/onsitelive-teams" className="text-navy hover:underline">Teams and limits</Link>.
      </p>
    </section>
  );
}
